import { useEffect, useState } from "react";
import { MessageCircle, Eye, RefreshCcw, CheckCircle2 } from "lucide-react";
import api, { errMsg, fmtUSD, fmtDate, STATUS_LABELS, STATUS_COLORS, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AdminCRM from "@/components/AdminCRM";

const SERVICE_LABELS = { repair: "Reparación", shipping: "Envío", export: "Exportación" };
const SERVICE_STATUS = { pending: "Pendiente", contacted: "Contactado", quoted: "Cotizado", closed: "Cerrado" };

function BidRow({ bid, onOpen }) {
  const lot = bid.lot || {};
  const vehicle = `${lot.year || ""} ${lot.make || ""} ${lot.model || ""}`.trim() || `Lote ${lot.lot_number || "—"}`;
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => onOpen(bid)} data-testid={`admin-bid-row-${bid.id}`}>
      <td className="py-3 px-4">
        <p className="font-medium text-slate-200 text-sm">{vehicle}</p>
        <p className="text-xs text-slate-500">#{lot.lot_number || "—"}</p>
      </td>
      <td className="py-3 px-4">
        <p className="text-sm text-slate-300">{bid.user?.name || "—"}</p>
        <p className="text-xs text-slate-500">{bid.user?.email}</p>
      </td>
      <td className="py-3 px-4 text-sm text-slate-300">{fmtUSD(bid.max_bid)}</td>
      <td className="py-3 px-4 text-sm text-slate-300">{fmtUSD(bid.deposit_amount)}</td>
      <td className="py-3 px-4 text-sm text-slate-400">{lot.sale_date || "—"}</td>
      <td className="py-3 px-4">
        <Badge className={`${STATUS_COLORS[bid.status]} border text-xs`} data-testid={`admin-bid-status-${bid.id}`}>{STATUS_LABELS[bid.status]}</Badge>
      </td>
    </tr>
  );
}

export default function Admin() {
  const [bids, setBids] = useState([]);
  const [payments, setPayments] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", final_price: "", note: "" });
  const [lotEdit, setLotEdit] = useState(null);
  const [refundCard, setRefundCard] = useState("us");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadAll = () => {
    api.get("/admin/bid-requests").then((r) => setBids(r.data)).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/service-requests").then((r) => setServices(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const openBid = (bid) => {
    setSelected(bid);
    setStatusForm({ status: bid.status, final_price: bid.final_price || "", note: "" });
    setLotEdit({ ...bid.lot });
  };

  const refreshSelected = async () => {
    loadAll();
    if (selected) {
      try {
        const { data } = await api.get(`/bid-requests/${selected.id}`);
        setSelected(data);
      } catch {}
    }
  };

  const setStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bid-requests/${selected.id}/status`, {
        status: statusForm.status,
        note: statusForm.note,
        final_price: statusForm.status === "won" ? Number(statusForm.final_price) : undefined,
      });
      toast.success("Estado actualizado");
      refreshSelected();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmBalance = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bid-requests/${selected.id}/confirm-balance`);
      toast.success("Pago del saldo confirmado");
      refreshSelected();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doRefund = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bid-requests/${selected.id}/refund`, { card_type: refundCard });
      toast.success(`Reembolsado ${fmtUSD(data.refund.refunded_amount)} (cuota ${data.refund.fee_pct}%)`);
      refreshSelected();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveLot = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/bid-requests/${selected.id}/lot`, { lot: lotEdit });
      toast.success("Datos del lote actualizados");
      refreshSelected();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        flat_fee: Number(settings.flat_fee), deposit_pct: Number(settings.deposit_pct),
        min_deposit: Number(settings.min_deposit), refund_fee_us: Number(settings.refund_fee_us),
        refund_fee_foreign: Number(settings.refund_fee_foreign),
        whatsapp_number: settings.whatsapp_number, contact_email: settings.contact_email,
        zelle_email: settings.zelle_email,
        wire_instructions: settings.wire_instructions,
      });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setServiceStatus = async (id, status) => {
    try {
      await api.post(`/admin/service-requests/${id}/status`, { status });
      toast.success("Solicitud de servicio actualizada");
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const filteredBids = filter === "all" ? bids : bids.filter((b) => b.status === filter);
  const wa = settings?.whatsapp_number || "13055550123";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12" data-testid="admin-dashboard">
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">Administración</h1>
      <p className="text-slate-400 text-sm mb-10">Gestiona solicitudes de puja, pagos, reembolsos y servicios.</p>

      <Tabs defaultValue="bids">
        <TabsList className="bg-[#111827] border border-white/10 mb-8">
          <TabsTrigger value="bids" data-testid="tab-bids">Solicitudes ({bids.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Pagos ({payments.length})</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Servicios ({services.length})</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Clientes</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="bids">
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-xs text-slate-400">Filtrar:</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="bids-filter" className="w-48 bg-[#0B0F1A] border-white/15 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="card-tech rounded-xl overflow-x-auto">
            <table className="w-full min-w-[760px]" data-testid="bids-table">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-4">Vehículo</th><th className="py-3 px-4">Cliente</th>
                  <th className="py-3 px-4">Puja máx.</th><th className="py-3 px-4">Depósito</th>
                  <th className="py-3 px-4">Subasta</th><th className="py-3 px-4">Estado</th>
                </tr>
              </thead>
              <tbody>
                {filteredBids.map((b) => <BidRow key={b.id} bid={b} onOpen={openBid} />)}
                {filteredBids.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-sm text-slate-500">Sin solicitudes</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="card-tech rounded-xl overflow-x-auto">
            <table className="w-full min-w-[720px]" data-testid="payments-table">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-4">Fecha</th><th className="py-3 px-4">Tipo</th>
                  <th className="py-3 px-4">Monto</th><th className="py-3 px-4">Estado</th><th className="py-3 px-4">Sesión</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.session_id} className="border-b border-white/5">
                    <td className="py-3 px-4 text-sm text-slate-300">{fmtDate(p.created_at)}</td>
                    <td className="py-3 px-4 text-sm text-slate-300">{p.type === "deposit" ? "Depósito" : p.type}</td>
                    <td className="py-3 px-4 text-sm text-slate-200 font-medium">{fmtUSD(p.amount)}</td>
                    <td className="py-3 px-4">
                      <Badge className={`border text-xs ${p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : p.payment_status === "refunded" ? "bg-slate-500/20 text-slate-300 border-slate-400/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                        {p.payment_status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500 font-mono">{p.session_id?.slice(0, 24)}...</td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-500">Sin transacciones</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="services">
          <div className="grid gap-4" data-testid="services-list">
            {services.map((s) => (
              <div key={s.id} className="card-tech rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-200 text-sm">{SERVICE_LABELS[s.type] || s.type}</p>
                  <p className="text-xs text-slate-500">{s.user?.name} · {s.user?.email} · {fmtDate(s.created_at)}</p>
                  {s.message && <p className="text-sm text-slate-400 mt-2 max-w-xl">{s.message}</p>}
                </div>
                <div className="flex items-center gap-3">
                  {s.user?.phone && (
                    <a href={`https://wa.me/${s.user.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hola ${s.user.name}, sobre tu solicitud de ${SERVICE_LABELS[s.type]} en CarBid...`)}`}
                      target="_blank" rel="noreferrer" data-testid={`service-wa-${s.id}`}>
                      <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full">
                        <MessageCircle className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> WhatsApp
                      </Button>
                    </a>
                  )}
                  <Select value={s.status} onValueChange={(v) => setServiceStatus(s.id, v)}>
                    <SelectTrigger data-testid={`service-status-${s.id}`} className="w-36 bg-[#0B0F1A] border-white/15 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SERVICE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
            {services.length === 0 && <div className="card-tech rounded-xl p-10 text-center text-sm text-slate-500">Sin solicitudes de servicio</div>}
          </div>
        </TabsContent>

        <TabsContent value="settings">
          {settings && (
            <div className="card-tech rounded-xl p-8 max-w-2xl" data-testid="settings-form">
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  ["flat_fee", "Comisión de bróker (USD)"], ["deposit_pct", "Depósito (% de puja máx.)"],
                  ["min_deposit", "Depósito mínimo (USD)"], ["refund_fee_us", "Cuota reembolso tarjeta US (%)"],
                  ["refund_fee_foreign", "Cuota reembolso tarjeta extranjera (%)"], ["whatsapp_number", "Número WhatsApp (solo dígitos)"],
                  ["contact_email", "Correo de contacto y notificaciones"],
                  ["zelle_email", "Correo Zelle"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-slate-300 text-xs">{label}</Label>
                    <Input data-testid={`setting-${k}`} value={settings[k]}
                      onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                      className="mt-1.5 bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500" />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <Label className="text-slate-300 text-xs">Instrucciones de wire</Label>
                  <Textarea data-testid="setting-wire" value={settings.wire_instructions}
                    onChange={(e) => setSettings({ ...settings, wire_instructions: e.target.value })}
                    className="mt-1.5 bg-[#0B0F1A] border-white/15 min-h-[90px]" />
                </div>
              </div>
              <Button data-testid="settings-save" onClick={saveSettings} disabled={busy}
                className="mt-6 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8">
                {busy ? "Guardando..." : "Guardar configuración"}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="customers">
          <AdminCRM leadsOnly={false} />
        </TabsContent>

        <TabsContent value="leads">
          <AdminCRM leadsOnly={true} />
        </TabsContent>
      </Tabs>

      {/* Dialog detalle de solicitud */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-50 max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="admin-detail-title">
                  {`${selected.lot?.year || ""} ${selected.lot?.make || ""} ${selected.lot?.model || ""}`.trim() || `Lote ${selected.lot?.lot_number || "—"}`}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-2">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <Badge className={`${STATUS_COLORS[selected.status]} border`}>{STATUS_LABELS[selected.status]}</Badge>
                  <span className="text-slate-400">Puja máx: <strong className="text-white">{fmtUSD(selected.max_bid)}</strong></span>
                  <span className="text-slate-400">Depósito: <strong className="text-white">{fmtUSD(selected.deposit_amount)}</strong></span>
                  {selected.final_price && <span className="text-slate-400">Final: <strong className="text-emerald-300">{fmtUSD(selected.final_price)}</strong></span>}
                </div>

                <div className="text-xs bg-[#0B0F1A] border border-white/10 rounded-lg p-4">
                  <p className="text-slate-300 font-semibold mb-1">Cliente</p>
                  <p className="text-slate-400">{selected.user?.name} · {selected.user?.email} · {selected.user?.phone || "sin teléfono"}</p>
                  {selected.user?.has_id_document && (
                    <a href={`${API}/users/${selected.user_id}/id-document`} target="_blank" rel="noreferrer"
                      data-testid="admin-view-id" className="text-[#2563EB] hover:text-blue-400 inline-flex items-center gap-1 mt-1.5">
                      <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Ver documento de identidad
                    </a>
                  )}
                </div>

                {/* Cambiar estado */}
                <div className="border border-white/10 rounded-lg p-5">
                  <p className="text-sm font-semibold mb-4">Actualizar estado</p>
                  <div className="flex flex-wrap gap-3">
                    <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                      <SelectTrigger data-testid="admin-status-select" className="w-48 bg-[#0B0F1A] border-white/15"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {statusForm.status === "won" && (
                      <Input data-testid="admin-final-price" type="number" placeholder="Precio final USD" value={statusForm.final_price}
                        onChange={(e) => setStatusForm({ ...statusForm, final_price: e.target.value })}
                        className="w-44 bg-[#0B0F1A] border-white/15" />
                    )}
                    <Input data-testid="admin-status-note" placeholder="Nota (opcional)" value={statusForm.note}
                      onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
                      className="flex-1 min-w-[160px] bg-[#0B0F1A] border-white/15" />
                    <Button data-testid="admin-status-save" onClick={setStatus} disabled={busy}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">Guardar</Button>
                  </div>
                </div>

                {/* Acciones de pago */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="border border-white/10 rounded-lg p-5">
                    <p className="text-sm font-semibold mb-2">Pago del saldo</p>
                    <p className="text-xs text-slate-500 mb-3">
                      {selected.balance_payment?.marked_at
                        ? `Cliente reportó ${selected.balance_payment.method?.toUpperCase()} ${selected.balance_payment.confirmed_at ? "(confirmado)" : "(sin confirmar)"}`
                        : "El cliente aún no reporta pago."}
                    </p>
                    <Button data-testid="admin-confirm-balance" size="sm" onClick={confirmBalance}
                      disabled={busy || !["won", "payment_due"].includes(selected.status)}
                      className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-full">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Confirmar pago recibido
                    </Button>
                  </div>
                  <div className="border border-white/10 rounded-lg p-5">
                    <p className="text-sm font-semibold mb-2">Reembolsar depósito</p>
                    {selected.refund ? (
                      <p className="text-xs text-emerald-300">Reembolsado: {fmtUSD(selected.refund.refunded_amount)} (cuota {selected.refund.fee_pct}%)</p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Select value={refundCard} onValueChange={setRefundCard}>
                          <SelectTrigger data-testid="admin-refund-card-type" className="w-36 bg-[#0B0F1A] border-white/15 h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="us">Tarjeta US</SelectItem>
                            <SelectItem value="foreign">Tarjeta extranjera</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button data-testid="admin-refund-btn" size="sm" onClick={doRefund} disabled={busy}
                          className="bg-amber-600 hover:bg-amber-700 text-white rounded-full">
                          <RefreshCcw className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Reembolsar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Editor de lote */}
                {lotEdit && (
                  <div className="border border-white/10 rounded-lg p-5">
                    <p className="text-sm font-semibold mb-4">Editor de datos del lote</p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {[["lot_number", "Lote #"], ["year", "Año"], ["make", "Marca"], ["model", "Modelo"],
                        ["mileage", "Millaje"], ["title_type", "Título"], ["location", "Ubicación"], ["sale_date", "Fecha subasta"], ["image_url", "URL imagen"]].map(([k, label]) => (
                        <div key={k}>
                          <Label className="text-[11px] text-slate-500">{label}</Label>
                          <Input data-testid={`admin-lot-${k}`} value={lotEdit[k] || ""}
                            onChange={(e) => setLotEdit({ ...lotEdit, [k]: e.target.value })}
                            className="mt-1 bg-[#0B0F1A] border-white/15 h-9 text-sm" />
                        </div>
                      ))}
                    </div>
                    <Button data-testid="admin-lot-save" size="sm" onClick={saveLot} disabled={busy}
                      className="mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">Guardar datos del lote</Button>
                  </div>
                )}

                {/* Historial */}
                <div className="border border-white/10 rounded-lg p-5">
                  <p className="text-sm font-semibold mb-3">Historial</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {[...(selected.status_history || [])].reverse().map((h, i) => (
                      <p key={i} className="text-xs text-slate-400">
                        <span className="text-slate-200 font-medium">{STATUS_LABELS[h.status] || h.status}</span> · {fmtDate(h.at)}{h.note ? ` — ${h.note}` : ""}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
